import React, { useCallback } from 'react';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { WriteBackOverrideRule } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { WriteBackOverrideRuleCondition } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { WriteBackOverrideRuleComparator } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { WriteBackOverrideRuleTransformation } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';

export const COMPARATOR_LABELS: Record<
  WriteBackOverrideRuleComparator,
  string
> = {
  greaterThan: 'Greater Than (>)',
  greaterThanOrEqual: 'Greater Than or Equal (>=)',
  lessThan: 'Less Than (<)',
  lessThanOrEqual: 'Less Than or Equal (<=)',
  equalTo: 'Equal To (=)',
};

export const DEFAULT_OVERRIDE_RULE: WriteBackOverrideRule = {
  conditions: [
    {
      comparator: 'greaterThanOrEqual',
      thresholdValue: 0,
    },
  ],
  overrideValue: 0,
};

export const DEFAULT_OVERRIDE_CONDITION: WriteBackOverrideRuleCondition = {
  comparator: 'greaterThanOrEqual',
  thresholdValue: 0,
};

export interface ConfigureOverrideRuleProps {
  rule: WriteBackOverrideRule;
  ruleIndex: number;
  onEdit: (index: number, item: Partial<WriteBackOverrideRule>) => void;
  onDelete: (index: number) => void;
}

export const ConfigureOverrideRule = ({
  rule,
  ruleIndex,
  onEdit,
  onDelete,
}: ConfigureOverrideRuleProps) => {
  const updateRule = useCallback(
    (partial: Partial<WriteBackOverrideRule>) => {
      onEdit(ruleIndex, partial);
    },
    [onEdit, ruleIndex],
  );

  const setConditions = useCallback(
    (conditions: WriteBackOverrideRuleCondition[]) => {
      updateRule({ conditions });
    },
    [updateRule],
  );

  const conditionsCrud = useCrudOperations({
    items: rule.conditions,
    setItems: setConditions,
  });

  const addNewCondition = useCallback(() => {
    conditionsCrud.onAdd(DEFAULT_OVERRIDE_CONDITION);
  }, [conditionsCrud]);

  const updateOverrideValue = useCallback(
    (value: string) => {
      const numericValue = parseFloat(value);
      updateRule({
        overrideValue: isNaN(numericValue) ? value : numericValue,
        transformation: undefined,
      });
    },
    [updateRule],
  );

  const useOverride = rule.transformation === undefined;

  const setUseOverride = useCallback(
    (useOverrideValue: boolean) => {
      if (useOverrideValue) {
        updateRule({
          overrideValue: 0,
          transformation: undefined,
        });
      } else {
        updateRule({
          overrideValue: undefined,
          transformation: {
            operation: 'multiply',
            factor: 1,
          },
        });
      }
    },
    [updateRule],
  );

  const transformationFactor =
    rule.transformation && 'factor' in rule.transformation
      ? rule.transformation.factor
      : 1;
  const transformationOp =
    rule.transformation && 'operation' in rule.transformation
      ? rule.transformation.operation
      : 'multiply';

  const setTransformationOperation = useCallback(
    (operation: 'multiply' | 'divide') => {
      updateRule({
        overrideValue: undefined,
        transformation: {
          operation,
          factor: transformationFactor,
        },
      });
    },
    [updateRule, transformationFactor],
  );

  const updateTransformation = useCallback(
    (transformation: WriteBackOverrideRuleTransformation) => {
      updateRule({
        overrideValue: undefined,
        transformation,
      });
    },
    [updateRule],
  );

  const deleteRule = useCallback(() => {
    onDelete(ruleIndex);
  }, [onDelete, ruleIndex]);

  return (
    <div className="border rounded p-4 space-y-4 bg-white">
      <div className="flex justify-between items-start">
        <h6 className="font-medium text-sm">Rule {ruleIndex + 1}</h6>
        <Button
          behavior={{
            type: 'onClick',
            onClick: deleteRule,
          }}
          text="Delete"
          size="small"
          variant="destructive"
        />
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">
            Conditions (all must match)
          </span>
          <Button
            behavior={{
              type: 'onClick',
              onClick: addNewCondition,
            }}
            text="+ Add Condition"
            size="small"
            variant="outline"
          />
        </div>

        {rule.conditions.map((condition, conditionIndex) => (
          <ConfigureOverrideRuleCondition
            key={conditionIndex}
            condition={condition}
            conditionIndex={conditionIndex}
            onEdit={conditionsCrud.onEdit}
            onDelete={conditionsCrud.onDelete}
          />
        ))}
      </div>

      <div className="space-y-3">
        <span className="text-sm font-medium block">Then</span>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2">
            <input
              className="form-radio"
              type="radio"
              name={`rule-${ruleIndex}-action`}
              checked={useOverride}
              onChange={() => setUseOverride(true)}
            />
            <span className="text-sm">Fixed value</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              className="form-radio"
              type="radio"
              name={`rule-${ruleIndex}-action`}
              checked={!useOverride && transformationOp === 'multiply'}
              onChange={() => setTransformationOperation('multiply')}
            />
            <span className="text-sm">Multiply score by</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              className="form-radio"
              name={`rule-${ruleIndex}-action`}
              checked={!useOverride && transformationOp === 'divide'}
              onChange={() => setTransformationOperation('divide')}
            />
            <span className="text-sm">Divide score by</span>
          </label>
        </div>

        {useOverride ? (
          <label className="space-y-1 flex flex-col">
            <span className="text-sm font-medium">Override Value</span>
            <input
              type="text"
              value={rule.overrideValue ?? ''}
              onChange={(e) => {
                updateOverrideValue(e.target.value);
              }}
              className="form-input border rounded p-2 text-sm"
              placeholder="Number or text (e.g., 100 or 'Excellent')"
            />
            <span className="text-xs text-gray-500">
              Can be a number or text value to write back
            </span>
          </label>
        ) : (
          <label className="space-y-1 flex flex-col max-w-xs">
            <span className="text-sm font-medium">Factor</span>
            <input
              type="number"
              step="any"
              min={transformationOp === 'divide' ? 0.0001 : undefined}
              value={transformationFactor}
              onChange={(e) => {
                const factor = e.target.valueAsNumber;
                if (!Number.isNaN(factor)) {
                  updateTransformation({
                    operation: transformationOp,
                    factor,
                  });
                }
              }}
              className="form-input border rounded p-2 text-sm"
            />
            <span className="text-xs text-gray-500">
              Score will be written as score × factor or score ÷ factor
            </span>
          </label>
        )}
      </div>
    </div>
  );
};

export interface ConfigureOverrideRuleConditionProps {
  condition: WriteBackOverrideRuleCondition;
  conditionIndex: number;
  onEdit: (
    index: number,
    item: Partial<WriteBackOverrideRuleCondition>,
  ) => void;
  onDelete: (index: number) => void;
}

export const ConfigureOverrideRuleCondition = ({
  condition,
  conditionIndex,
  onEdit,
  onDelete,
}: ConfigureOverrideRuleConditionProps) => {
  const updateCondition = useCallback(
    (partial: Partial<WriteBackOverrideRuleCondition>) => {
      onEdit(conditionIndex, partial);
    },
    [onEdit, conditionIndex],
  );

  const updateComparator = useCallback(
    (comparator: WriteBackOverrideRuleComparator) => {
      updateCondition({ comparator });
    },
    [updateCondition],
  );

  const updateThresholdValue = useCallback(
    (thresholdValue: number) => {
      updateCondition({ thresholdValue });
    },
    [updateCondition],
  );

  const deleteCondition = useCallback(() => {
    onDelete(conditionIndex);
  }, [onDelete, conditionIndex]);

  return (
    <div className="flex items-center gap-2 bg-gray-50 p-2 rounded">
      <span className="text-xs text-gray-600 whitespace-nowrap">Score</span>
      <select
        value={condition.comparator}
        onChange={(e) => {
          updateComparator(e.target.value as WriteBackOverrideRuleComparator);
        }}
        className="form-select border rounded p-1 text-sm flex-shrink-0"
      >
        {Object.entries(COMPARATOR_LABELS).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      <input
        type="number"
        value={condition.thresholdValue}
        onChange={(e) => {
          updateThresholdValue(e.target.valueAsNumber);
        }}
        className="form-input border rounded p-1 text-sm w-24"
      />
      <Button
        behavior={{
          type: 'onClick',
          onClick: deleteCondition,
        }}
        text="×"
        size="small"
        variant="destructive"
      />
    </div>
  );
};
